'use client'

import {DPEuProjectNode, DPPageNode} from 'types/DPTypes'
import {
	EuProjects,
	EuProjectsDetail,
	EuProjectsFilters,
	EuProjectsItemType
} from 'euprojects/euprojects'
import {Fragment, Suspense, useEffect} from 'react'

import {Blocks} from 'blocks/blocks'
import {Hero} from 'blocks/hero/hero'
import {PING_URL} from 'util/urls'
import {Paragraph} from 'blocks/paragraph'
import {euProjectsSlugLocale} from './euprojects.slug'
import unfetch from 'unfetch'
import {useApp} from 'AppContext'
import {useTranslation} from 'util/i18'

export const EuProjectsClient: React.FC<{
	node: DPPageNode
	projects: EuProjectsItemType[]
	filters: EuProjectsFilters
}> = ({node, projects, filters}) => {
	const t = useTranslation()
	useEffect(() => {
		if (node) {
			const pingUrl = PING_URL(node?.node_id)
			if (pingUrl) unfetch(pingUrl).catch(() => {})
		}
	}, [node])

	if (!node || !node.blocks || !node.blocks.length)
		return (
			<Blocks blocks={[]}>
				<Hero title={t.euprojects.title} />
				<Suspense>
					<EuProjects projects={projects} filters={filters} />
				</Suspense>
			</Blocks>
		)
	return (
		<Blocks blocks={[]}>
			{node.blocks.map((block, i) => {
				if (i > 0) return <Paragraph {...block} key={block.id} />
				if (
					block._type === 'hero' ||
					block._type === 'herohome' ||
					block._type === 'producthero'
				) {
					return (
						<Fragment key={block.id}>
							<Paragraph {...block} />
							<Suspense>
								<EuProjects projects={projects} filters={filters} />
							</Suspense>
						</Fragment>
					)
				}
				return (
					<Fragment key={block.id}>
						<Hero title={t.euprojects.title} />
						<Suspense>
							<EuProjects projects={projects} filters={filters} />
						</Suspense>
						<Paragraph {...block} />
					</Fragment>
				)
			})}
		</Blocks>
	)
}

export const EuProjectsClientDetailPage: React.FC<{
	node: DPEuProjectNode
	project: EuProjectsItemType
	filters: EuProjectsFilters
	custom_parents: {
		url: string
		title: string
	}[]
}> = ({node, project, filters, custom_parents}) => {
	const {lang} = useApp()
	const t = useTranslation()

	const blocks = node && node.blocks ? node.blocks : []

	return (
		<Blocks blocks={[]}>
			<Hero
				title={project.title}
				description={project.description}
				customParents={[
					...custom_parents,
					{
						url: `/${lang}${euProjectsSlugLocale(lang)}`,
						title: t.euprojects.title
					}
				]}
			/>
			<EuProjectsDetail
				project={project}
				filters={filters}
				euprojects_slug={euProjectsSlugLocale(lang)}
				blocks={blocks}
				logo={node ? node.logo : null}
			/>
		</Blocks>
	)
}
